import ChannelsResource from 'flow/api/channels';
import ChatsResource from 'flow/api/chats';

import Actions from 'flow/actions';
import {
    networkErrorDispatcher,
    httpGetErrorDispatcher,
} from 'flow/actions/app-actions';

export const activateChannelProfile =
    (channelKey, profileDTO, callback) => (dispatch) => {
        ChannelsResource.setActiveProfile(
            {
                onResult(json) {
                    dispatch(receiveChannelActiveProfile(channelKey, json));
                    callback?.();
                },

                onError: networkErrorDispatcher(dispatch),
            },
            channelKey,
            profileDTO,
        );
    };

export const fetchWidgetChannel =
    (channelKey, ...include) =>
    (dispatch) => {
        ChannelsResource.getChannel(
            {
                onResult(json) {
                    dispatch(receiveChannel(json, channelKey));
                },
                onError: httpGetErrorDispatcher(dispatch),
            },
            channelKey,
            include.join(),
        );
    };

export const fetchChannelStatSummary =
    (channelKey, errorCallback = networkErrorDispatcher) =>
    (dispatch) => {
        ChannelsResource.getChannelStatSummary(
            {
                onResult(json) {
                    dispatch(receiveChannelStatSummary(channelKey, json));
                },
                onError: errorCallback(dispatch),
            },
            channelKey,
        );
    };

export const fetchPaginatedArchivedChannelSessions =
    (channelKey, chatMessageKey) => (dispatch) => {
        ChannelsResource.getPaginatedArchivedChats(
            {
                onResult(json) {
                    dispatch(
                        receivePaginatedArchivedExternalChats(
                            channelKey,
                            chatMessageKey,
                            json,
                        ),
                    );
                },
                onError: networkErrorDispatcher(dispatch),
            },
            channelKey,
            chatMessageKey,
        );
    };

export const fetchWidgetChannels =
    (...include) =>
    (dispatch) => {
        ChannelsResource.getChannels(
            {
                onResult(json) {
                    dispatch(receiveChannels(json));
                },
                onError: httpGetErrorDispatcher(dispatch),
            },
            include.join(),
        );
    };

export const fetchChannelMembers = (channelKey) => (dispatch) => {
    ChannelsResource.getChannelMembers(
        {
            onResult(json) {
                dispatch(receiveChannelMembers(json, channelKey));
            },
            onError: httpGetErrorDispatcher(dispatch),
        },
        channelKey,
    );
};

export const addChannelMembers =
    (channelKey, extensionEntityKeys) => (dispatch) => {
        ChannelsResource.addChannelMembers(
            {
                onResult(json) {
                    dispatch(receiveChannelMembers(json, channelKey));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            channelKey,
            extensionEntityKeys,
        );
    };

export const removeChannelMember =
    (channelKey, channelMemberKey) => (dispatch) => {
        ChannelsResource.removeChannelMember(
            {
                onResult(json) {
                    dispatch(receiveChannelMembers(json, channelKey));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            channelKey,
            channelMemberKey,
        );
    };

export const updateChannelMember =
    (channelEntityKey, channelMemberEntityKey, channelMemberDTO) =>
    (dispatch) => {
        ChannelsResource.updateChannelMember(
            {
                onResult(json) {
                    dispatch(receiveChannelMember(channelEntityKey, json));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            channelEntityKey,
            channelMemberEntityKey,
            channelMemberDTO,
        );
    };

export const sendWidgetReply = (sessionKey, clientKey, text, channelKey) => {
    const messageDTO = {
        message: text,
    };

    return (dispatch) => {
        ChannelsResource.createChatMessage(
            {
                onResult(json) {
                    dispatch(
                        receiveChannelMessage(sessionKey, channelKey, json),
                    );
                },

                onError: networkErrorDispatcher(dispatch),
            },
            sessionKey,
            clientKey,
            messageDTO,
        );
    };
};

export const fetchWidgetChatMessages =
    (chatSessionKey, fromMessageKey, ...include) =>
    (dispatch) => {
        ChannelsResource.getChatMessages(
            {
                onResult(json) {
                    dispatch(receiveWidgetMessages(chatSessionKey, json));
                },
                onError: httpGetErrorDispatcher(dispatch),
            },
            chatSessionKey,
            fromMessageKey,
            include.join(),
        );
    };

export const fetchOlderWidgetMessages =
    (chatSessionKey, chatMessageEntityKey, ...include) =>
    (dispatch) => {
        ChatsResource.getMessageHistory(
            {
                onResult(json) {
                    dispatch(receiveOlderWidgetMessages(chatSessionKey, json));
                },
                onError: httpGetErrorDispatcher(dispatch),
            },
            chatSessionKey,
            chatMessageEntityKey,
            include.join(),
        );
    };

export const markWidgetMessageRead = (
    channelKey,
    chatSessionKey,
    chatMessageEntityKey,
) => {
    const messageDTO = {
        key: chatMessageEntityKey,
    };

    return (dispatch) => {
        ChatsResource.updateMessage(
            {
                onResult(/*void*/) {
                    dispatch(
                        receiveChannelMessageRead(
                            channelKey,
                            chatSessionKey,
                            messageDTO,
                        ),
                    );
                },

                onError: networkErrorDispatcher(dispatch),
            },
            chatSessionKey,
            messageDTO,
        );
    };
};

export const sendIsTyping = (sessionKey, extensionKey, typing) => {
    const extension = {
        key: extensionKey,
    };
    const chatSession = {
        key: sessionKey,
    };
    const isTypingDTO = { extension, chatSession, typing };

    return (dispatch) => {
        ChannelsResource.isTyping(
            {
                onResult(/*void*/) {
                    // void
                },

                onError: networkErrorDispatcher(dispatch),
            },
            sessionKey,
            isTypingDTO,
        );
    };
};

export const receiveIsTyping = (
    channelKey,
    sessionKey,
    extensionKey,
    isTyping,
) => ({
    type: Actions.RESULT_TYPING,
    channelKey,
    sessionKey,
    extensionKey,
    isTyping,
});

export const channelMessageCompose = (chatSessionKey, message) => {
    return {
        type: Actions.CHANNEL_COMPOSER_UPDATE,
        key: chatSessionKey,
        message: message,
    };
};

export const fetchChannelCalendar = (entityKey) => (dispatch) => {
    ChannelsResource.getCalendar(
        {
            onResult(json) {
                dispatch(receiveChannelCalendar(entityKey, json));
            },
            onError: httpGetErrorDispatcher(dispatch),
        },
        entityKey,
    );
};

export const updateChannelOpeningHours =
    (entityKey, openingHours) => (dispatch) => {
        ChannelsResource.setOpeningHours(
            {
                onResult(json) {
                    dispatch(receiveChannelOpeningHours(entityKey, json));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            entityKey,
            openingHours,
        );
    };

export const createChannelCalendarEvent =
    (entityKey, calendarEvent) => (dispatch) => {
        ChannelsResource.addEvent(
            {
                onResult(json) {
                    dispatch(
                        receiveChannelCreateCalendarEvent(entityKey, json),
                    );
                    dispatch(fetchWidgetChannel(entityKey, 'all'));
                },
                onError: networkErrorDispatcher(dispatch),
            },
            entityKey,
            calendarEvent,
        );
    };

export const updateChannelCalendarEvent =
    (entityKey, calendarEventKey, calendarEvent) => (dispatch) => {
        ChannelsResource.setEvent(
            {
                onResult(json) {
                    dispatch(
                        receiveChannelUpdateCalendarEvent(
                            entityKey,
                            calendarEventKey,
                            json,
                        ),
                    );
                    dispatch(fetchWidgetChannel(entityKey, 'all'));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            entityKey,
            calendarEventKey,
            calendarEvent,
        );
    };

export const removeChannelCalendarEvent =
    (entityKey, calendarEventKey) => (dispatch) => {
        ChannelsResource.deleteEvent(
            {
                onResult(json) {
                    dispatch(
                        receiveChannelRemoveCalendarEvent(entityKey, json),
                    );
                    dispatch(fetchWidgetChannel(entityKey, 'all'));
                    dispatch(fetchChannelCalendar(entityKey));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            entityKey,
            calendarEventKey,
        );
    };

export const archiveChatSession = (channelEntityKey, chatSessionEntityKey) => {
    return (dispatch) => {
        ChannelsResource.archiveChatSession(
            {
                onResult(json) {
                    dispatch(receiveArchived(channelEntityKey, json));
                },
                onError: networkErrorDispatcher(dispatch),
            },
            channelEntityKey,
            chatSessionEntityKey,
        );
    };
};

export const unarchiveChatSession = (
    channelEntityKey,
    chatSessionEntityKey,
) => {
    return (dispatch) => {
        ChannelsResource.unarchiveChatSession(
            {
                onResult(json) {
                    dispatch(receiveUnarchived(channelEntityKey, json));
                },
                onError: networkErrorDispatcher(dispatch),
            },
            channelEntityKey,
            chatSessionEntityKey,
        );
    };
};

export const getChannelSession = (channelEntityKey, chatSessionEntityKey) => {
    return (dispatch) => {
        ChannelsResource.getChannelSession(
            {
                onResult(json) {
                    dispatch(receiveChannelSession(channelEntityKey, json));
                },
                onError: httpGetErrorDispatcher(dispatch),
            },
            channelEntityKey,
            chatSessionEntityKey,
        );
    };
};

export const updateProfile = (channelEntityKey, profileDTO) => {
    const profileEntityKey = profileDTO.get('key');
    return (dispatch) => {
        ChannelsResource.updateProfile(
            {
                onResult(json) {
                    dispatch(
                        receiveChannelUpdatedProfile(
                            channelEntityKey,
                            profileEntityKey,
                            json,
                        ),
                    );
                },
                onError: networkErrorDispatcher(dispatch),
            },
            channelEntityKey,
            profileDTO,
        );
    };
};

export const updateProfiles = (channelEntityKey, profileDTOs) => {
    const profileKeys = profileDTOs.map((profile) => profile.get('key'));
    return (dispatch) => {
        ChannelsResource.updateProfiles(
            {
                onResult(json) {
                    dispatch(
                        receiveChannelUpdatedProfiles(
                            channelEntityKey,
                            profileKeys,
                            json,
                        ),
                    );
                },
                onError: networkErrorDispatcher(dispatch),
            },
            channelEntityKey,
            profileDTOs,
        );
    };
};

export const channelSessionBadgeCount = (channelEntityKey) => ({
    type: Actions.RESULT_CHANNEL_SESSION_BADGE_COUNT,
    key: channelEntityKey,
});

const receiveChannelSession = (channelEntityKey, json) => ({
    type: Actions.RESULT_CHANNEL_SESSION,
    key: channelEntityKey,
    data: json,
});

const receiveChannelActiveProfile = (channelKey, json) => ({
    type: Actions.RESULT_CHANNEL_ACTIVE_PROFILE,
    channelKey: channelKey,
    data: json,
});

const receiveChannelUpdatedProfile = (channelKey, profileKey, json) => ({
    type: Actions.RESULT_CHANNEL_UPDATED_PROFILE,
    channelKey,
    profileKey,
    data: json,
});

const receiveChannelUpdatedProfiles = (channelKey, profileKeys, json) => ({
    type: Actions.RESULT_CHANNEL_UPDATED_PROFILES,
    channelKey,
    profileKeys,
    data: json,
});

export const receiveChannelMessageRead = (channelKey, sessionKey, message) => ({
    type: Actions.RESULT_WIDGET_MESSAGE_READ,
    key: sessionKey,
    channelKey: channelKey,
    data: message,
});

export const receiveChannelMessage = (sessionKey, channelKey, json) => ({
    type: Actions.RESULT_WIDGET_MESSAGE,
    key: sessionKey,
    channelKey: channelKey,
    data: json,
});

const receiveOlderWidgetMessages = (sessionKey, json) => ({
    type: Actions.RESULT_OLDER_WIDGET_MESSAGES,
    key: sessionKey,
    data: json,
});

const receiveChannel = (channel, channelKey) => ({
    type: Actions.RESULT_WIDGET_CHANNEL,
    data: channel,
    channelKey: channelKey,
});

const receiveChannelStatSummary = (channelKey, json) => ({
    type: Actions.RESULT_CHANNEL_STAT_SUMMARY,
    key: channelKey,
    data: json,
});

const receiveChannels = (channels) => ({
    type: Actions.RESULT_WIDGET_CHANNELS,
    data: channels,
});

const receiveChannelMembers = (channelMembers, channelEntityKey) => ({
    type: Actions.RESULT_CHANNEL_MEMBERS,
    data: channelMembers,
    channelKey: channelEntityKey,
});

const receiveChannelMember = (channelEntityKey, json) => ({
    type: Actions.RESULT_UPDATE_CHANNEL_MEMBER,
    data: json,
    channelKey: channelEntityKey,
});

const receiveWidgetMessages = (chatSessionKey, messages) => ({
    type: Actions.RESULT_WIDGET_MESSAGES,
    key: chatSessionKey,
    data: messages,
});

export const resetNumberOfUnreadMessages = (
    chatSessionKey,
    channelEntityKey,
) => ({
    type: Actions.RESET_NUMBER_OF_UNREAD_MESSAGES,
    key: chatSessionKey,
    channelKey: channelEntityKey,
});

const receiveChannelCalendar = (entityKey, json) => ({
    type: Actions.RESULT_CHANNEL_CALENDAR,
    key: entityKey,
    data: json,
});

const receiveChannelOpeningHours = (entityKey, json) => ({
    type: Actions.RESULT_CHANNEL_OPENING_HOURS,
    key: entityKey,
    data: json,
});

const receiveChannelCreateCalendarEvent = (entityKey, json) => ({
    type: Actions.RESULT_CHANNEL_ADD_CALENDAR_EVENT,
    key: entityKey,
    data: json,
});

const receiveChannelUpdateCalendarEvent = (
    entityKey,
    calendarEventKey,
    json,
) => ({
    type: Actions.RESULT_CHANNEL_UPDATE_CALENDAR_EVENT,
    key: entityKey,
    originalCalendarEventKey: calendarEventKey,
    data: json,
});

const receiveChannelRemoveCalendarEvent = (entityKey, json) => ({
    type: Actions.RESULT_CHANNEL_REMOVE_CALENDAR_EVENT,
    key: entityKey,
    data: json,
});
export const receiveArchived = (channelKey, json) => ({
    type: Actions.RESULT_ARCHIVE_CHANNEL_SESSION,
    key: channelKey,
    data: json,
});

export const receiveUnarchived = (channelKey, json) => ({
    type: Actions.RESULT_UNARCHIVE_CHANNEL_SESSION,
    key: channelKey,
    data: json,
});

const receivePaginatedArchivedExternalChats = (
    channelKey,
    chatMessageKey,
    json,
) => ({
    type: Actions.RESULT_PAGINATED_ARCHIVED_CHANNEL_SESSIONS,
    key: channelKey,
    chatMessageKey,
    data: json,
});
